import React, { useEffect, useState, useCallback, useMemo } from "react";
import { balanceSide } from './balanceSide';
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
  Alert,
  Spinner,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { AsyncPaginate } from "react-select-async-paginate";
import { authFetch } from "../utils/authFetch";

const LIMIT = 100;

export default function FisaCont() {
  const today = new Date();
  const yyyy = today.getFullYear();

  const [from, setFrom] = useState(new Date(yyyy, today.getMonth(), 1));
  const [to, setTo] = useState(today);
  const [account, setAccount] = useState(null);
  const [contact, setContact] = useState(null);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loadedAccountType, setLoadedAccountType] = useState(null);

  // Helper to format Date objects to YYYY-MM-DD
  const formatDateString = (date) => {
    if (!date) return "";
    if (typeof date === "string") return date;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const loadAccountOptions = useCallback(async (inputValue, _loaded, additional) => {
    const page = additional?.page ?? 1;
    const q = (inputValue || "").trim();
    const skip = (page - 1) * LIMIT;
    try {
      const res = await authFetch(
        `/accounts?search=${encodeURIComponent(q)}&skip=${skip}&limit=${LIMIT}&sort_by=code`
      );
      const items = Array.isArray(res)
        ? res
        : Array.isArray(res?.items)
          ? res.items
          : [];
      
      const options = items.map((a) => ({
        value: a.id,
        label: `${a.code} - ${a.name}`,
        bxswitch: a.bxswitch,
        type: a.type
      }));

      const hasMore = items.length === LIMIT;

      return {
        options,
        hasMore,
        additional: { page: page + 1 },
      };
    } catch (e) {
      console.error(e);
      return { options: [], hasMore: false, additional: { page } };
    }
  }, []);

  const loadContactOptions = useCallback(async (inputValue, _loaded, additional) => {
    const page = additional?.page ?? 1;
    const q = (inputValue || "").trim();
    const skip = (page - 1) * LIMIT;
    try {
      const res = await authFetch(
        `/contacte/?search=${encodeURIComponent(q)}&skip=${skip}&limit=${LIMIT}`
      );
      const items = Array.isArray(res)
        ? res
        : Array.isArray(res?.items)
          ? res.items
          : [];
      const total =
        typeof res?.total === "number"
          ? res.total
          : typeof res?.count === "number"
            ? res.count
            : undefined;

      const options = items.map((c) => ({
        value: c.id,
        label: `${c.bxdenumire}${c.bxcodfiscal ? ` (${c.bxcodfiscal})` : ""}`,
      }));

      const hasMore =
        typeof total === "number"
          ? skip + items.length < total
          : items.length === LIMIT;

      return {
        options,
        hasMore,
        additional: { page: page + 1 },
      };
    } catch (e) {
      console.error(e);
      return { options: [], hasMore: false, additional: { page } };
    }
  }, []);

  const isSubledger = useMemo(() => {
    if (!account) return false;
    return account.bxswitch === 1 || account.bxswitch === 11;
  }, [account]);

  // Reset contact filter if account is not a subledger
  useEffect(() => {
    if (!isSubledger) {
      setContact(null);
    }
  }, [isSubledger]);

  const loadData = async () => {
    if (!account) {
      setErr("Te rugăm să selectezi un cont.");
      return;
    }
    setBusy(true);
    setErr("");
    setLoaded(false);
    setRows([]);
    try {
      const fromStr = formatDateString(from);
      const toStr = formatDateString(to);
      const contactId = contact?.value;
      const contactQuery = contactId ? `&contact_id=${contactId}` : "";
      
      const res = await authFetch(
        `/rapoarte/fisa-cont?from=${fromStr}&to=${toStr}&account_id=${account.value}${contactQuery}`
      );
      
      if (Array.isArray(res)) {
        setRows(res);
        setLoadedAccountType(account.type);
        setLoaded(true);
      } else {
        setErr("Format răspuns invalid de la server.");
      }
    } catch (e) {
      setErr(e?.message || "Eroare la încărcare fișă de cont.");
    } finally {
      setBusy(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const fmt2 = (n) => {
    const val = Number(n ?? 0);
    if (Math.abs(val) < 0.005) return "-";
    return val.toLocaleString("ro-RO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <div className="p-3">
      <Card className="shadow-sm border-0 rounded-4 p-4 mb-4">
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div>
            <h4 className="fw-bold text-dark mb-1">Fișă de Cont</h4>
            <p className="text-muted small mb-0">
              Vizualizează mișcările contabile detaliate pentru contul selectat în perioada aleasă.
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-primary" onClick={handlePrint} className="rounded-3 fw-semibold">
              Printează
            </Button>
            <Button variant="primary" onClick={loadData} disabled={busy} className="rounded-3 fw-semibold">
              {busy ? "Se încarcă..." : "Generează Raport"}
            </Button>
          </div>
        </div>

        <Form className="bg-light p-3 rounded-4 mb-4 border border-light-subtle">
          <Row className="g-3 align-items-end">
            <Col xs={12} md={3}>
              <Form.Label className="fw-semibold text-secondary small mb-1">Cont Contabil</Form.Label>
              <AsyncPaginate
                value={account}
                loadOptions={loadAccountOptions}
                onChange={setAccount}
                placeholder="Alege cont..."
                className="shadow-sm border-0"
              />
            </Col>

            {isSubledger && (
              <Col xs={12} md={3}>
                <Form.Label className="fw-semibold text-secondary small mb-1">Partener (opțional)</Form.Label>
                <AsyncPaginate
                  value={contact}
                  loadOptions={loadContactOptions}
                  onChange={setContact}
                  isClearable
                  placeholder="Alege partener..."
                  className="shadow-sm border-0"
                />
              </Col>
            )}

            <Col xs={12} md={4}>
              <Form.Label className="fw-semibold text-secondary small mb-1">Perioadă</Form.Label>
              <div className="d-flex align-items-center gap-2">
                <DatePicker
                  selected={from}
                  onChange={(date) => setFrom(date)}
                  dateFormat="dd.MM.yyyy"
                  className="form-control rounded-3 border-0 shadow-sm"
                />
                <span className="text-secondary">-</span>
                <DatePicker
                  selected={to}
                  onChange={(date) => setTo(date)}
                  dateFormat="dd.MM.yyyy"
                  className="form-control rounded-3 border-0 shadow-sm"
                />
              </div>
            </Col>
          </Row>
        </Form>

        {err && (
          <Alert variant="danger" className="rounded-3 mb-4">
            {err}
          </Alert>
        )}

        {loaded && (
          <div className="table-responsive rounded-3 border border-light-subtle">
            {busy ? (
              <div className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
                <p className="text-muted mt-2 mb-0">Se prelucrează fișa de cont...</p>
              </div>
            ) : (
              <Table hover bordered size="sm" className="align-middle mb-0 text-nowrap" style={{ fontSize: "0.85rem" }}>
                <thead>
                  <tr className="bg-primary text-white text-center align-middle">
                    <th className="py-2 bg-primary text-white" style={{ width: "95px" }}>Dată</th>
                    <th className="py-2 bg-primary text-white" style={{ width: "120px" }}>Tip Document</th>
                    <th className="py-2 bg-primary text-white" style={{ width: "100px" }}>Număr</th>
                    <th className="py-2 bg-primary text-white">Explicație</th>
                    <th className="py-2 bg-primary text-white" style={{ width: "110px" }}>Debit (RON)</th>
                    <th className="py-2 bg-primary text-white" style={{ width: "110px" }}>Credit (RON)</th>
                    {isSubledger ? (
                      <th className="py-2 bg-primary text-white" style={{ width: "180px" }}>Partener</th>
                    ) : (
                      <th className="py-2 bg-primary text-white" style={{ width: "110px" }}>Cont Corespondent</th>
                    )}
                    <th className="py-2 bg-primary text-white" style={{ width: "120px" }}>Sold (RON)</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={isSubledger ? 8 : 8} className="text-center text-muted py-4">
                        Nu există tranzacții înregistrate pentru acest cont în perioada selectată.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, idx) => {
                      const isInitial = row.doc_date === "Initial";
                      const isTotal = row.explicatie === "TOTAL (perioadă)";
                      const isSoldFinal = row.explicatie === "SOLD final";
                      
                      let rowClass = "";
                      if (isInitial) rowClass = "table-light text-muted fw-semibold";
                      else if (isTotal) rowClass = "table-secondary fw-bold border-top-2";
                      else if (isSoldFinal) rowClass = "table-primary fw-bold";

                      return (
                        <tr key={idx} className={rowClass}>
                          <td className="text-center">{row.doc_date === "Initial" ? "Inițial" : row.doc_date}</td>
                          <td className="text-center">{row.tip}</td>
                          <td className="text-center">{row.doc_no}</td>
                          <td>{row.explicatie}</td>
                          <td className="text-end font-monospace">{fmt2(row.debit)}</td>
                          <td className="text-end font-monospace">{fmt2(row.credit)}</td>
                          {isSubledger ? (
                            <td>{row.partner_name}</td>
                          ) : (
                            <td className="text-center fw-semibold">{row.coresp_code}</td>
                          )}
                          <td className="text-end font-monospace fw-semibold">
                            {isTotal ? "" : fmt2(Math.abs(row.sold)) + balanceSide(row.sold, loadedAccountType)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
